import React, { useRef } from 'react';
import cn from 'classnames';

import { FormField } from '@creditflowresearch/uilib/Form/FormField';
import { FormError } from '@creditflowresearch/uilib/Form/FormError';

import useTextAreaAutosize from './useTextAreaAutosize';

import styles from '@creditflowresearch/uilib/Input/input.module.scss';
import formStyles from '../shared/form.module.scss';

export interface IProps {
  /**
   * Meta information for the form field (e.g., validation state)
   */
  meta?: any,
  /**
   * The input object containing the current value and handlers (e.g., from Final Form or React Hook Form)
   */
  input?: any,
  /**
   * The label text or component displayed next to the input
   */
  label?: React.ReactNode,
  /**
   * Optional CSS class name for custom styling of the textarea
   */
  textareaClassName?: string,
  /**
   * Optional CSS class name for custom styling of the input container
   */
  className?: string,
  /**
   * Optional CSS class name for custom styling of the container
   */
  containerClassName?: string,
  /**
   * Whether to enable drag-and-drop functionality (if applicable)
   */
  useDrop?: boolean,
  /**
   * Whether the input should have a narrower width than usual
   */
  isNarrow?: boolean,
  /**
   * The name attribute for the textarea input
   */
  name?: string,
  /**
   * Number of rows to display in the textarea
   */
  rows?: number,
  /**
   * Message to display when the countdown is active
   */
  countdownMessage?: string,
  /**
   * Whether to enable the countdown message
   */
  isEnabledCountdownMessage?: boolean,
  /**
   * Whether to show an error message for the input
   */
  isErrorMessage?: boolean,
  /**
   * Whether to highlight the border when there is an error
   */
  highlightBorderOnError?: boolean,
  /**
   * Optional CSS class name for custom styling of the form field container
   */
  formFieldClassName?: string,
  /**
   * Unique identifier for testing purposes
   */
  dataTest?: string,
  /**
   * Whether the textarea is disabled
   */
  disabled?: boolean,
  /**
   * Whether the textarea should auto-resize based on content
   */
  autosize?: boolean,
  /**
   * Maximum length of input in the textarea
   */
  maxLength?: number,
  /**
   * Maximum height of the textarea
   */
  maxHeight?: number,
}

const Textarea = ({
  label,
  className,
  textareaClassName,
  containerClassName,
  meta,
  input,
  useDrop = false,
  isNarrow = false,
  name,
  countdownMessage,
  isEnabledCountdownMessage = false,
  isErrorMessage = true,
  highlightBorderOnError = true,
  dataTest,
  autosize = false,
  maxLength,
  maxHeight,
  formFieldClassName,
  ...textAreaOtherProps
}: IProps) => {
  const textAreaRef = useRef(null);

  const textareaCls = cn(styles.formInputTextArea, textareaClassName, {
    [styles.withAutosize]: autosize,
    // @ts-ignore
    [styles.isError]: highlightBorderOnError && meta && meta.touched && (meta.error || meta.warning),
  });

  const inputProps = { ...input };
  const charactersRemaining = maxLength - (inputProps.value?.length || 0);
  const isPlural = charactersRemaining !== 1;
  const countdownMsg = countdownMessage || `character${ isPlural ? 's' : '' } remaining`;

  const {
    textAreaHeight,
    parentHeight,
    onChangeWithAutosize,
  } = useTextAreaAutosize(
    {
      textAreaRef,
      maxLength,
      maxHeight,
      inputProps,
      isDisabled: !autosize,
    },
  );

  if (autosize) {
    inputProps.onChange = onChangeWithAutosize;
  }

  if (!useDrop) {
    inputProps.onDragStart = () => {};
    inputProps.onDrop = () => {};
  }

  return (
    <FormField
      className={ formFieldClassName }
      isNarrow={ isNarrow }
      isValidationFeedback={ !!(meta && meta.touched && meta.error) }
      dataTest={ dataTest }
    >
      <div className={ className }>
        { label && (
          <label
            className={ formStyles.formLabel }
            htmlFor={ input.name }
          >
            { label }
          </label>
        ) }
        <div
          className={ containerClassName }
          style={ autosize ? { minHeight: parentHeight } : {} }
        >
          <textarea
            style={ autosize ? { height: textAreaHeight } : {} }
            ref={ textAreaRef }
            className={ textareaCls }
            maxLength={ maxLength }
            name={ name }
            data-test={ name }
            { ...textAreaOtherProps }
            { ...inputProps }
          />
          { isErrorMessage && <FormError { ...meta } /> }
        </div>
        { (isEnabledCountdownMessage && maxLength) && (
          <p className={ styles.textareaCharacters }> { charactersRemaining } { countdownMsg } </p>
        ) }
      </div>
    </FormField>
  );
};

export default Textarea;
